import React, {
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  forwardRef,
  useMemo,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import { getValidChildrenCount } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, {
  stylePropType,
  StylableComponent,
  flattenStyle,
  StyleProp,
} from '@adsk/alloy-react-theme';

import { defaultRenderNoAutocompleteResults } from '../../helpers/defaultRenderers';
import { stripUnknownProps } from '../../helpers/internalHelpers';
import {
  GetMenuProps,
  DataAttributes,
  FooterProps,
  GetFooterProps,
} from '../../types';

export type MenuProps = StylableComponent<HTMLUListElement> &
  DataAttributes & {
    /** Whether Dropdown menu is open */
    isOpen: boolean;
    /** Toggle loading state */
    isLoading?: boolean;
    /** Whether to show autocomplete-specific features */
    isAutocomplete?: boolean;
    /** Function to get props applied to the <ul> element.  This is returned from the dropdown hook */
    getMenuProps: GetMenuProps;
    /** Function to get props applied to the menu footer.  This is returned from the dropdown hook */
    getFooterProps?: GetFooterProps;
    /** The menu width */
    width?: number;
    /**
     * Render a component that will wrap the <ul> element, but still be contained within the overlay.
     * Use this to enable virtualization
     */
    renderWrapper?: (
      props: HTMLAttributes<HTMLDivElement>,
    ) => ReactElement | null;
    /** Render a component that will appear at the bottom of the menu */
    renderFooter?: (props: FooterProps) => ReactNode;
    /** Render a component that will appear when isLoading is true */
    renderLoading?: () => ReactNode;
    /** Render a component that will appear at the top of the menu */
    renderHeader?: () => ReactNode;
    /** Render a component that will appear when there are no menu items */
    renderNoAutocompleteResults?: () => ReactNode;
  };

const unusedProps = [
  'selectItem',
  'selectedItem',
  'selectedItems',
  'placement',
  'addSelectedItem', // This doesn't get used but is returned from multiselect hooks
  'removeSelectedItem', // This doesn't get used but is returned from multiselect hooks
  'isGroupHeader',
  'highlightedIndex',
  'groupedItems',
  // These only get used by DropdownMenu and not Menu
  'allowCollapsibleGroups',
  'showGroupItemCount',
  'setExpandedGroupKey',
  'expandedGroupKey',
  'setSelectedGroupKey',
  'selectedGroupKey',
  'showGroupHeaders',
  'allowGroupFiltering',
  'setHighlightedIndex',
  'refToFocusOnClick',
  'showEmptyGroups',
  'dropdownId',
];

const scrollStyle: StyleProp = {
  position: 'relative',
  overflowY: 'auto',
};

const LoadingSpinner = () => (
  <div
    css={{
      display: 'flex',
      justifyContent: 'center',
      margin: `${theme.spacing.M}px 0px`,
    }}
  >
    <ProgressRing />
  </div>
);

const defaultRenderLoading = () => <LoadingSpinner />;

const Menu = forwardRef<HTMLUListElement, MenuProps>(
  (
    {
      style = {},
      width,
      isOpen,
      isLoading = false,
      isAutocomplete = false,
      getMenuProps,
      getFooterProps,
      renderWrapper,
      renderFooter,
      renderLoading = defaultRenderLoading,
      renderHeader,
      renderNoAutocompleteResults = defaultRenderNoAutocompleteResults,
      children,
      ...props
    },
    ref,
  ) => {
    const showHeaderOrFooter = useMemo(
      () => isOpen && (!!renderFooter || !!renderHeader),
      [isOpen, renderFooter, renderHeader],
    );

    const wrapperStyle: StyleProp = useMemo(
      () => ({
        ...(!isOpen && {
          height: 0,
        }),
      }),
      [isOpen],
    );

    const childrenCount = useMemo(
      () => getValidChildrenCount(children),
      [children],
    );

    const parentStyle: StyleProp = useMemo(
      () => [
        {
          boxShadow: theme.shadows.highElevation,
          backgroundColor: theme.tokens.colors.background.primary.value,
          maxHeight:
            // if there are no items found with autocomplete and a custom render
            // is provided, set maxHeight to a larger number so that the menu will take
            // on the height of whatever custom render is provided
            isAutocomplete &&
            childrenCount <= 0 &&
            renderNoAutocompleteResults !== defaultRenderNoAutocompleteResults
              ? 1000
              : 300,
          maxWidth: 'calc(100% + 70px)',
          width,
          borderRadius: theme.borderRadius.medium,
        },
        style,
      ],
      [
        childrenCount,
        isAutocomplete,
        renderNoAutocompleteResults,
        style,
        width,
      ],
    );

    const {
      ref: containerRef,
      style: containerStyle,
      ...menuProps
    } = useMemo(() => getMenuProps(), [getMenuProps]);

    const strippedMenuProps = useMemo(
      () => stripUnknownProps(menuProps, unusedProps),
      [menuProps],
    );
    const strippedProvidedProps = useMemo(
      () => stripUnknownProps(props, unusedProps),
      [props],
    );

    const itemsOrMessage = useMemo(
      () => (childrenCount > 0 ? children : renderNoAutocompleteResults()),
      [children, childrenCount, renderNoAutocompleteResults],
    );

    const renderMenu = useCallback(
      ({ internalStyle }: { internalStyle?: StyleProp } = {}) => (
        <ul
          ref={ref}
          css={[
            theme.typography.labelMedium,
            {
              listStyleType: 'none',
              userSelect: 'none',
              outline: 'none',
              padding: 0,
              margin: 0,
            },
            internalStyle,
          ]}
          data-testid="dropdown-menu-items"
          {...strippedMenuProps}
          {...strippedProvidedProps}
        >
          {isOpen &&
            (isLoading
              ? renderLoading()
              : isAutocomplete
                ? itemsOrMessage
                : children)}
        </ul>
      ),
      [
        children,
        isAutocomplete,
        isLoading,
        isOpen,
        itemsOrMessage,
        ref,
        renderLoading,
        strippedMenuProps,
        strippedProvidedProps,
      ],
    );

    const renderContent = useCallback(
      ({ contentStyle }: { contentStyle: StyleProp }) =>
        renderWrapper
          ? renderWrapper({
              style: flattenStyle([contentStyle, wrapperStyle]),
              children: renderMenu(),
            })
          : renderMenu({ internalStyle: contentStyle }),
      [renderMenu, renderWrapper, wrapperStyle],
    );

    return (
      <Overlay.Container
        data-testid={'Dropdown__menu'}
        ref={containerRef as Ref<HTMLDivElement>}
        style={containerStyle as StyleProp}
      >
        {showHeaderOrFooter ? (
          <div
            css={[
              {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
              parentStyle,
            ]}
          >
            {renderHeader?.()}
            {renderContent({ contentStyle: scrollStyle })}
            {renderFooter?.(getFooterProps?.() || {})}
          </div>
        ) : (
          renderContent({ contentStyle: [scrollStyle, parentStyle] })
        )}
      </Overlay.Container>
    );
  },
);

Menu.displayName = 'Menu';

Menu.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The menu width */
  width: PropTypes.number,
  /** The content of the Menu */
  children: PropTypes.any,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
  /** Toggle loading state */
  isLoading: PropTypes.bool,
  /** Function to get props applied to the <ul> element.  This is returned from the dropdown hook */
  getMenuProps: PropTypes.func.isRequired,
  /**
   * Render a component that will wrap the <ul> element, but still be contained within the overlay.
   * Use this is to enable virtualization
   */
  renderWrapper: PropTypes.func,
  /** Render a component that will appear at the bottom of the menu */
  renderFooter: PropTypes.func,
  /** Render a component that will appear when isLoading is true */
  renderLoading: PropTypes.func,
};

export default Menu;
